import { Alert, Button, IconButton } from '@mui/material';
import type { ReactElement } from 'react';
import React, { useCallback, useMemo } from 'react';
import { useEntitySecurityPath } from '../../../auth/Security';
import EllipsisText from '../../../components/EllipsisText';
import Loading from '../../../components/Loading';
import { BUTTON_VARIANTS } from '../../../constants/constants';
import { useDeepLinkURL } from '../../../deepLink';
import I18n from '../../../i18n/I18n';
import { Icons } from '../../../icons/Icons';
import { H, V } from '../../../Layout';
import { failedToastMessage, successToastMessage } from '../../../utils/commonUtils';
import { SHARE_SETTINGS_MODE } from '../../constants';
import { shareSettingsDataToShareSettingsViewModel } from '../../data/utils';
import { useShareSettings } from '../hooks/useShareSettings';
import ShareSettings from './ShareSettings';

export type ShareableEntity = {
    restricted?: boolean;
    sharedWithAccounts?: Parameters<typeof shareSettingsDataToShareSettingsViewModel>[0]['sharedWithAccounts'];
    sharedWithDepartments?: Parameters<typeof shareSettingsDataToShareSettingsViewModel>[0]['sharedWithDepartments'];
    createdBy?: string;
    createdByName?: string;
};

type EntityShareSettingsProps<T extends ShareableEntity> = {
    entityId: string | null;
    entity: T | null;
    title?: string | ReactElement;
    isLoading?: boolean;
    onClose: () => void;
    onAfterShareSettingsSave?: () => Promise<unknown> | void;
    makeShareSettingsScopeId: (entityId: string) => string;
    deepLinkUIStatePath?: string;
    successToastToken?: string;
    failureToastToken?: string;
};

export default function EntityShareSettings<T extends ShareableEntity>({
    entityId,
    entity,
    title = '',
    isLoading = false,
    onClose,
    onAfterShareSettingsSave,
    makeShareSettingsScopeId,
    deepLinkUIStatePath,
    successToastToken = 'common.share.settings.update.success',
    failureToastToken = 'common.share.settings.update.failure'
}: EntityShareSettingsProps<T>) {
    const { saveShareSettings, checkHasUnsavedShareSettings, isSavingShareSettings } = useShareSettings();
    const { getDeepLinkURL } = useDeepLinkURL();
    const defaultDeepLinkUIStatePath = useEntitySecurityPath();

    const effectiveDeepLinkUIStatePath = deepLinkUIStatePath ?? defaultDeepLinkUIStatePath;

    const shareSettingsScopeId = useMemo(
        () => (entityId ? makeShareSettingsScopeId(entityId) : ''),
        [entityId, makeShareSettingsScopeId]
    );

    const initialShareSettings = useMemo(() => {
        if (!entity) return null;

        return shareSettingsDataToShareSettingsViewModel({
            restricted: Boolean(entity.restricted),
            sharedWithAccounts: entity.sharedWithAccounts ?? [],
            sharedWithDepartments: entity.sharedWithDepartments ?? [],
            entityCreatedBy: entity.createdBy,
            entityCreatedByName: entity.createdByName
        });
    }, [entity]);

    const hasUnsavedChanges = shareSettingsScopeId ? checkHasUnsavedShareSettings(shareSettingsScopeId) : false;

    const handleSave = useCallback(async () => {
        if (!entityId || !shareSettingsScopeId) {
            return;
        }

        try {
            const deepLinkURL = getDeepLinkURL(`${effectiveDeepLinkUIStatePath}.id`);

            await saveShareSettings(shareSettingsScopeId, { id: entityId }, deepLinkURL);
            successToastMessage(successToastToken);
            await onAfterShareSettingsSave?.();
            onClose();
        } catch {
            failedToastMessage(failureToastToken);
        }
    }, [
        entityId,
        effectiveDeepLinkUIStatePath,
        failureToastToken,
        getDeepLinkURL,
        onAfterShareSettingsSave,
        onClose,
        saveShareSettings,
        shareSettingsScopeId,
        successToastToken
    ]);

    if (isLoading) {
        return (
            <V sx={{ maxHeight: '100%', px: 1 }}>
                <H sx={{ width: '100%', justifyContent: 'space-between', p: 1, pb: 1 }}>
                    <EllipsisText text={title} sx={{ flexGrow: 2, fontSize: 'medium' }} />
                    <IconButton size="small" onClick={onClose} aria-label="close">
                        <Icons.Close fontSize="small" />
                    </IconButton>
                </H>
                <Loading />
            </V>
        );
    }

    if (!entityId || !entity || !initialShareSettings) {
        return (
            <V gap={1} sx={{ justifyContent: 'center', px: 1 }}>
                <H sx={{ width: '100%', justifyContent: 'space-between', p: 1, pb: 1 }}>
                    <EllipsisText text={title} sx={{ flexGrow: 2, fontSize: 'medium' }} />
                    <IconButton size="small" onClick={onClose} aria-label="close">
                        <Icons.Close fontSize="small" />
                    </IconButton>
                </H>
                <Alert severity="error">
                    <I18n token={failureToastToken} />
                </Alert>
                <Button variant={BUTTON_VARIANTS.text} onClick={onClose}>
                    <I18n token="dialog.back" />
                </Button>
            </V>
        );
    }

    return (
        <V sx={{ maxHeight: '100%', px: 1 }}>
            <H sx={{ width: '100%', justifyContent: 'space-between', p: 1, pb: 1 }}>
                <EllipsisText text={title} sx={{ flexGrow: 2, fontSize: 'medium' }} />
                <IconButton size="small" onClick={onClose} aria-label="close">
                    <Icons.Close fontSize="small" />
                </IconButton>
            </H>
            <V sx={{ p: 1, overflowY: 'auto' }}>
                <ShareSettings
                    mode={SHARE_SETTINGS_MODE.update}
                    scopeId={shareSettingsScopeId}
                    initialShareSettings={initialShareSettings}
                />
            </V>
            <H sx={{ justifyContent: 'flex-end', p: 1 }}>
                <Button
                    variant={BUTTON_VARIANTS.text}
                    sx={{ mr: 2 }}
                    onClick={onClose}
                    disabled={isSavingShareSettings}
                >
                    <I18n token="dialog.cancel" />
                </Button>
                <Button
                    variant={BUTTON_VARIANTS.contained}
                    onClick={handleSave}
                    disabled={!hasUnsavedChanges || isSavingShareSettings}
                >
                    <I18n token="form.button.save" />
                </Button>
            </H>
        </V>
    );
}
